/**
 * Which boats are emphasised on the live map.
 *
 * A fleet map draws every selected boat in its own colour, which answers "where is everyone" but
 * not "where is THAT one" — with a dozen similar tracks the eye needs help. Clicking a boat in the
 * live BSP/Grade overlay puts it in this set and the track layer emphasises it, dimming the rest.
 *
 * Highlighting is DISTINCT FROM SELECTION and must stay that way. Selection decides which boats
 * exist on the map at all (and drives subscriptions, tables, hydration); highlighting is a purely
 * visual emphasis over that set, so turning it off leaves everything on screen exactly as it was.
 * Conflating the two would mean a click that looks like emphasis silently drops boats from the
 * stream.
 *
 * Keyed by uppercased source NAME rather than by id, for the same reason the track and alarm layers
 * resolve by name: the frontend's `source_id` and the stream's frame id are different numbering
 * schemes that happen to collide, and names are stable across a session reset.
 */

import { createSignal } from 'solid-js';

const [highlighted, setHighlighted] = createSignal<ReadonlySet<string>>(new Set());

const normalize = (name: string): string => String(name ?? '').trim().toUpperCase();

/** Reactive set of emphasised boat names (uppercased). Empty means "emphasise nothing". */
export const highlightedSources = highlighted;

/** True when at least one boat is emphasised — i.e. when the others should be dimmed. */
export const hasHighlightedSources = (): boolean => highlighted().size > 0;

export const isSourceHighlighted = (name: string): boolean => {
  const key = normalize(name);
  return key !== '' && highlighted().has(key);
};

/**
 * Add or remove one boat. Replaces the Set rather than mutating it: Solid compares by reference, so
 * an in-place `add`/`delete` would leave every consumer showing the previous emphasis.
 */
export const toggleHighlightedSource = (name: string): void => {
  const key = normalize(name);
  if (key === '') return;
  const next = new Set(highlighted());
  if (next.has(key)) next.delete(key);
  else next.add(key);
  setHighlighted(next);
};

export const clearHighlightedSources = (): void => {
  if (highlighted().size === 0) return; // no-op rather than a pointless reference change
  setHighlighted(new Set());
};

/** Drop names that are no longer on the map, so a stale emphasis cannot outlive its boat. */
export const retainHighlightedSources = (available: Iterable<string> | null | undefined): void => {
  const current = highlighted();
  if (current.size === 0) return;
  // Null/undefined is "no opinion about the selection", not "nothing is selected" — pruning against
  // it would clear every highlight, and spreading it would throw.
  if (available == null) return;
  const keep = new Set([...available].map(normalize).filter((n) => n !== ''));
  const next = new Set([...current].filter((n) => keep.has(n)));
  if (next.size !== current.size) setHighlighted(next);
};
